import { useState, useCallback, useMemo, useRef } from 'react';
import { CrmBoardService } from '../services/crm-board.service';
import { CrmColumnService } from '../services/crm-column.service';
import { CrmLeadService } from '../services/crm-lead.service';
import { MoveLeadRequest } from '../services/requests/lead.request';
import { BoardColumnWithLeads } from '../services/responses/board-column.response';
import { BoardDetailResponse } from '../services/responses/board.response';
import { LeadResponse } from '../services/responses/lead.response';

export const useCrmKanban = () => {
    const [board, setBoard] = useState<BoardDetailResponse | null>(null);
    const [loading, setLoading] = useState(false);

    /// Mostrar ganhos e perdidos por coluna; em ref também, pois loadBoard e refreshBoard leem direto.
    const [showClosed, setShowClosedState] = useState(false);
    const showClosedRef = useRef(false);

    const setShowClosed = useCallback((value: boolean) => {
        showClosedRef.current = value;
        setShowClosedState(value);
    }, []);

    const boardService = useMemo(() => new CrmBoardService(), []);
    const columnService = useMemo(() => new CrmColumnService(), []);
    const leadService = useMemo(() => new CrmLeadService(), []);

    const loadBoard = useCallback(async (boardId: string) => {
        setLoading(true);
        try {
            const res = await boardService.getById(boardId, showClosedRef.current);
            setBoard(res.data.data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [boardService]);

    const refreshBoard = useCallback(async (boardId: string) => {
        try {
            const res = await boardService.getById(boardId, showClosedRef.current);
            setBoard(res.data.data);
        } catch (e) {
            console.error(e);
        }
    }, [boardService]);

    const reorderColumns = useCallback(async (boardId: string, orderedIds: string[]) => {
        await columnService.reorder(boardId, orderedIds);
    }, [columnService]);

    const moveLead = useCallback(async (leadId: string, payload: MoveLeadRequest) => {
        await leadService.move(leadId, payload);
    }, [leadService]);

    const reorderLeads = useCallback(async (columnId: string, orderedIds: string[]) => {
        await leadService.reorder(columnId, orderedIds);
    }, [leadService]);

    const applyOptimisticColumnReorder = useCallback((newColumns: BoardColumnWithLeads[]) => {
        setBoard((prev) => (prev ? { ...prev, columns: newColumns } : prev));
    }, []);

    const applyOptimisticLeadMove = useCallback(
        (leadId: string, sourceColumnId: string, targetColumnId: string, targetIndex: number) => {
            setBoard((prev) => {
                if (!prev) return prev;
                const cols = prev.columns.map((c) => ({ ...c, leads: [...c.leads] }));
                const srcCol = cols.find((c) => c.id === sourceColumnId);
                const tgtCol = cols.find((c) => c.id === targetColumnId);
                if (!srcCol || !tgtCol) return prev;

                const leadIdx = srcCol.leads.findIndex((l) => l.id === leadId);
                if (leadIdx === -1) return prev;
                const [lead] = srcCol.leads.splice(leadIdx, 1);
                const updatedLead: LeadResponse = { ...lead, columnId: targetColumnId };
                tgtCol.leads.splice(targetIndex, 0, updatedLead);

                return { ...prev, columns: cols };
            });
        },
        []
    );

    return {
        board,
        loading,
        showClosed,
        setShowClosed,
        loadBoard,
        refreshBoard,
        reorderColumns,
        moveLead,
        reorderLeads,
        applyOptimisticColumnReorder,
        applyOptimisticLeadMove,
        setBoard,
    };
};
